import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getGideonPrayerPoints,
  GIDEON_RHYTHM_EVENTS,
  GIDEON_LAUNCH_ISO,
} from '../data/gideonSeed';
import { getGideonWhatsAppInviteUrl } from '../data/gideonConstants';
import GideonEnlistmentForm from '../components/gideon/GideonEnlistmentForm';
import GideonLaunchCarousel from '../components/gideon/GideonLaunchCarousel';
import GideonCalendar from '../components/gideon/GideonCalendar';

const GideonForcePage = () => {
  const { t } = useLanguage();
  const prayerPoints = getGideonPrayerPoints();
  const currentPrayerPoint = prayerPoints[0];
  const whatsappUrl = getGideonWhatsAppInviteUrl();

  const heroCopy = t('gideon.hero', {});
  const overviewCopy = t('gideon.overview', {});
  const missionCopy = t('gideon.mission', {});
  const expectationsCopy = t('gideon.expectations', {});
  const strategyCopy = t('gideon.strategy', {});
  const needCopy = t('gideon.need', {});
  const profileCopy = t('gideon.profile', {});
  const whereCopy = t('gideon.whereNext', {});

  const missionPoints = Array.isArray(missionCopy.points) ? missionCopy.points : [];
  const expectations = Array.isArray(expectationsCopy.items) ? expectationsCopy.items : [];
  const expectationDetails = Array.isArray(expectationsCopy.details) ? expectationsCopy.details : [];
  const strategyItems = Array.isArray(strategyCopy.items) ? strategyCopy.items : [];
  const profilePoints = Array.isArray(profileCopy.points) ? profileCopy.points : [];
  const whereItems = Array.isArray(whereCopy.items) ? whereCopy.items : [];

  const launchPretty = new Date(
    parseInt(GIDEON_LAUNCH_ISO.slice(0, 4), 10),
    parseInt(GIDEON_LAUNCH_ISO.slice(5, 7), 10) - 1,
    parseInt(GIDEON_LAUNCH_ISO.slice(8, 10), 10)
  ).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gideon-ink text-gideon-parchment">
      {/* Hero — camouflage / tactical aesthetic (brochure-aligned), no dot overlay */}
      <section className="relative overflow-hidden border-b border-gideon-khaki/30">
        <div className="absolute inset-0 bg-gradient-to-br from-gideon-moss via-gideon-olive to-gideon-ink" />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(-45deg, transparent, transparent 12px, rgba(255,255,255,.06) 12px, rgba(255,255,255,.06) 13px)',
          }}
        />
        <div className="relative z-10 container-max px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <p className="text-gideon-sand/90 text-xs sm:text-sm uppercase tracking-[0.35em] mb-4 font-semibold">
            {t('gideon.hero.kicker', 'YFC Trinidad & Tobago · Prayer Movement')}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-4 text-white drop-shadow-sm">
            {heroCopy.title || 'Gideon Force'}
          </h1>
          <p className="text-xl sm:text-2xl text-gideon-parchment font-semibold mb-2 max-w-3xl mx-auto">
            {heroCopy.subtitle || 'Mighty Warrior, the Lord is with you'}
          </p>
          <p className="text-sm sm:text-base text-gideon-sand/95 italic mb-6">
            — {heroCopy.scripture || 'Judges 6:12'}
          </p>
          <p className="text-sm text-gideon-parchment/85 max-w-2xl mx-auto">
            {t('gideon.hero.launchLine', 'National launch')}: <span className="font-semibold text-white">{launchPretty}</span>
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="#enlist"
              className="inline-flex items-center px-6 py-3 rounded-xl bg-prayer-red hover:bg-prayer-red-light text-white font-bold transition-colors shadow-lg"
            >
              {t('gideon.cta.enlistNow', 'Enlist Now')}
            </a>
            <a
              href="#calendar"
              className="inline-flex items-center px-6 py-3 rounded-xl border-2 border-gideon-khaki/80 text-gideon-parchment hover:bg-white/5 font-semibold transition-colors"
            >
              {t('gideon.hero.viewCalendar', 'View calendar')}
            </a>
          </div>
        </div>
      </section>

      {/* Launch imagery */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gideon-ink to-gideon-moss/40">
        <div className="container-max max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
            {t('gideon.media.title', 'Chaguanas launch')}
          </h2>
          <p className="text-center text-gideon-sand/90 mb-8 max-w-2xl mx-auto text-sm sm:text-base">
            {t('gideon.media.subtitle', 'Moments from the Gideon Force launch — thank you for joining the line.')}
          </p>
          <GideonLaunchCarousel />
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gideon-parchment/[0.06]">
        <div className="container-max max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
            {overviewCopy.title || 'The Vanguard Unit'}
          </h2>
          <p className="text-lg text-gideon-parchment/90 leading-relaxed">
            {overviewCopy.description}
          </p>
          <p className="mt-8 text-sm text-gideon-sand/85 leading-relaxed border border-gideon-khaki/30 rounded-xl p-6 bg-gideon-moss/30">
            {overviewCopy.regionalNote ||
              t(
                'gideon.overview.regionalNote',
                'A project of Youth For Christ — Trinidad and Tobago. Together with believers across twelve regions, six warriors per region lock arms with the national core team.'
              )}
          </p>
        </div>
      </section>

      {/* The need */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 border-y border-gideon-khaki/25 bg-gideon-moss/20">
        <div className="container-max max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 text-center">
            {needCopy.title || 'The Midianite crisis'}
          </h2>
          <p className="text-gideon-parchment/90 leading-relaxed text-center">{needCopy.body}</p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container-max max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            {missionCopy.title || 'Our Mission'}
          </h2>
          <ul className="space-y-4">
            {missionPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-2 h-2 bg-gideon-khaki rounded-sm mt-2 flex-shrink-0" />
                <span className="text-gideon-parchment/95">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Gideon profile */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gideon-parchment/[0.05]">
        <div className="container-max max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gideon-sand mb-2 uppercase tracking-wide">
            {profileCopy.kicker || 'The Gideon profile'}
          </h2>
          <p className="text-xl font-bold text-white mb-8">{profileCopy.title || 'Not by might nor by power'}</p>
          <ul className="text-left space-y-4 max-w-xl mx-auto">
            {profilePoints.map((point, i) => (
              <li key={i} className="flex gap-3 text-gideon-parchment/95">
                <span className="text-gideon-khaki font-bold">→</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Core Expectations */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center">
          {expectationsCopy.title || 'Core Expectations'}
        </h2>
        <div className="container-max grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {expectations.map((item, i) => (
            <div
              key={i}
              className="rounded-xl border border-gideon-khaki/35 bg-gradient-to-b from-gideon-moss/50 to-gideon-ink/80 p-6 text-center shadow-lg"
            >
              <p className="font-bold text-white mb-2">{item}</p>
              {expectationDetails[i] && (
                <p className="text-sm text-gideon-sand/85 leading-relaxed">{expectationDetails[i]}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Strategy */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gideon-moss/25 border-y border-gideon-khaki/25">
        <div className="container-max max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            {strategyCopy.title || 'Our Strategy'}
          </h2>
          <ul className="space-y-3">
            {strategyItems.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-gideon-khaki mt-0.5">◆</span>
                <span className="text-gideon-parchment/95">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Rhythm — brochure commitments */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container-max max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            {t('gideon.rhythm.sectionTitle', 'Operational rhythm')}
          </h2>
          <div className="space-y-4">
            {GIDEON_RHYTHM_EVENTS.map((ev) => (
              <div
                key={ev.id}
                className="rounded-xl border border-gideon-khaki/30 bg-gideon-moss/25 px-5 py-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2"
              >
                <p className="font-semibold text-white">{t(ev.titleKey)}</p>
                <p className="text-sm text-gideon-sand/90 sm:text-right max-w-md">{t(ev.detailKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where to from here */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gideon-parchment/[0.05]">
        <div className="container-max max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            {whereCopy.title || 'Where to from here'}
          </h2>
          <ol className="list-decimal list-inside space-y-4 text-gideon-parchment/95">
            {whereItems.map((item, i) => (
              <li key={i} className="leading-relaxed pl-1 marker:text-gideon-khaki">
                {item}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Stay connected */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 border-t border-gideon-khaki/25 bg-gradient-to-r from-gideon-moss/40 via-gideon-ink to-gideon-moss/40">
        <div className="container-max max-w-3xl mx-auto text-center">
          <h2 className="text-xl font-bold text-white mb-4">{t('gideon.connect.title', 'Stay in the loop')}</h2>
          <p className="text-gideon-sand/90 text-sm sm:text-base mb-6 leading-relaxed">
            {t(
              'gideon.connect.body',
              'Prayer points live on the Prayer Movement app. Enlist below to receive updates and optionally join our WhatsApp circle.'
            )}
          </p>
          {whatsappUrl ? (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold transition-colors"
            >
              {t('gideon.connect.whatsappCta', 'Open WhatsApp community')}
            </a>
          ) : (
            <p className="text-sm text-gideon-sand/70 italic">{t('gideon.connect.whatsappPlaceholder', 'WhatsApp invite link is configured when provided by the team.')}</p>
          )}
        </div>
      </section>

      {/* Enlistment */}
      <section id="enlist" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 scroll-mt-20 bg-gideon-parchment/[0.04]">
        <div className="container-max max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 text-center">
            {t('gideon.enlistment.title')}
          </h2>
          <p className="text-gideon-sand/90 text-center mb-8">{t('gideon.enlistment.subtitle')}</p>
          <div className="rounded-2xl border border-gideon-khaki/35 bg-gideon-moss/15 p-6 sm:p-8 backdrop-blur-sm">
            <GideonEnlistmentForm />
          </div>
        </div>
      </section>

      {/* Prayer Points */}
      {currentPrayerPoint && (
        <section id="prayer-points" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 scroll-mt-20 border-t border-gideon-khaki/20">
          <div className="container-max max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 text-center">
              {t('gideon.prayerPoints.title')}
            </h2>
            <p className="text-gideon-sand/90 text-center mb-8">{t('gideon.prayerPoints.subtitle')}</p>
            <div className="rounded-2xl border border-gideon-khaki/30 bg-gideon-moss/25 p-6 sm:p-8">
              <p className="text-sm font-semibold text-gideon-khaki mb-2">
                {currentPrayerPoint.weekLabel} · {currentPrayerPoint.focus}
              </p>
              <p className="text-sm text-gideon-sand/85 italic mb-6">{currentPrayerPoint.focalScripture}</p>
              <ul className="space-y-3">
                {currentPrayerPoint.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gideon-parchment/95 leading-relaxed">
                    <span className="text-gideon-khaki shrink-0">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Calendar */}
      <section id="calendar" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 scroll-mt-20 bg-gideon-parchment/[0.05]">
        <div className="container-max max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 text-center">
            {t('gideon.events.title')}
          </h2>
          <p className="text-gideon-sand/90 text-center mb-10">{t('gideon.events.subtitle')}</p>
          <GideonCalendar />
        </div>
      </section>

      <section className="py-12 px-4 border-t border-gideon-khaki/20">
        <div className="container-max text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gideon-khaki hover:text-white font-medium transition-colors"
          >
            ← {t('navigation.home', 'Home')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GideonForcePage;
